import {
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  DashboardOutlined,
  DownOutlined,
  FileTextOutlined,
  LockOutlined,
  MessageOutlined,
  NotificationOutlined,
  ProjectOutlined,
  RightOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { suppliersApi } from '../../api/suppliers.api'
import { messageCenterApi } from '../../api/messageCenter.api'
import { algorithmApi } from '../../api/algorithm.api'
import { platformSnapshotApi } from '../../api/platformSnapshot.api'
import { queryKeys } from '../../api/queryKeys'
import { usePermissions } from '../../hooks/usePermissions'
import type { AdminSection } from '../../config/permissions'
import { UPCOMING_BUSINESS_MODULES, UPCOMING_MINI_APPS, type UpcomingModule } from '../../config/upcomingModules'
import { useLogo } from './useLogo'
import { MoreMenu } from './MoreMenu'
import iconUrl from '../../assets/brand/icon.svg'
import './shell.css'

function usePendingSupplierCount(enabled: boolean) {
  const { data } = useQuery({
    queryKey: queryKeys.suppliers({ statusFilter: 'pending', search: '', cursor: 'first' }),
    queryFn: () => suppliersApi.list({ verificationStatus: 'pending', limit: 100 }),
    staleTime: 60_000,
    enabled,
  })
  return data?.data.data?.suppliers.length ?? 0
}

/**
 * `supplyOnly`: a page inside an open section that only ADMIN_CONTROL_ALLOWED_EMAILS
 * admins (Todd, 2026-10-29) and the Supplier role (Todd, 3 Nov 2026) may open —
 * the API refuses everyone else.
 */
type Leaf = { key: string; label: string; section: AdminSection; supplyOnly?: boolean }
/** A group inside a group (Mini-Apps → Creatives). Two levels is the limit. */
type SubGroup = { key: string; label: string; badge?: number; children: Leaf[] }
type GroupChild = Leaf | SubGroup
type Item =
  | (Leaf & { icon: ReactNode; badge?: number })
  | { key: string; icon: ReactNode; label: string; badge?: number; children: GroupChild[] }

const isActive = (pathname: string, key: string) => pathname === key || pathname.startsWith(`${key}/`)

const isSubGroup = (c: GroupChild): c is SubGroup => 'children' in c

/** A group's pages, sub-groups flattened. */
const leavesOf = (children: GroupChild[]): Leaf[] => children.flatMap((c) => (isSubGroup(c) ? c.children : [c]))

const holdsPage = (pathname: string, children: GroupChild[]) => leavesOf(children).some((c) => isActive(pathname, c.key))

const toLeaves = (modules: readonly UpcomingModule[]): Leaf[] =>
  modules.map((m) => ({ key: m.path, label: m.name, section: 'upcomingModules' }))

/** Same as the website's: nothing for 0, "99+" above 99. */
const formatBadge = (n?: number) => (!n ? null : n > 99 ? '99+' : String(n))

interface SidebarProps {
  /**
   * `rail` is the floating glass rail (tablet and desktop): icons only, widening
   * over the page on hover or keyboard focus — the website's web-app menu.
   * `panel` is always open, for the phone drawer.
   */
  variant: 'rail' | 'panel'
}

export function Sidebar({ variant }: SidebarProps) {
  const { pathname } = useLocation()
  const logoUrl = useLogo()
  const { level, homePath, canSeeSupplyInsights } = usePermissions()
  // A menu item shows only if the role's permission matrix lets it open the
  // section at all — the menu should not offer a link that just 403s.
  const visible = (section: AdminSection) => level(section) !== 'none'
  const visibleLeaf = (leaf: Leaf) => visible(leaf.section) && (!leaf.supplyOnly || canSeeSupplyInsights)
  // Don't fetch the pending-supplier count for a role that can't list suppliers:
  // the request would only 403.
  const pendingSuppliers = usePendingSupplierCount(visible('suppliers'))

  // Every item, in display order, with the section it belongs to. Groups vanish
  // when none of their children are visible.
  const allItems: Item[] = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard', section: 'dashboard' },
    // The supply dashboard is the "Dashboard" of a role that has it but not the
    // main dashboard (Supplier) — for everyone else it stays off the menu.
    ...(visible('dashboard')
      ? []
      : [{ key: '/supply-dashboard', icon: <DashboardOutlined />, label: 'Dashboard', section: 'supplyDashboard' as const }]),
    { key: '/users', icon: <TeamOutlined />, label: 'Users', section: 'users' },
    {
      // One report per department (docs/monthly-reports-plan.md). The original
      // Monthly Report is the Marketing one — its URL stays /monthly-report.
      key: 'monthly-reports-group',
      icon: <BarChartOutlined />,
      label: 'Monthly Reports',
      children: [
        { key: '/monthly-reports/users', label: 'Users', section: 'reportUsers' },
        { key: '/monthly-report', label: 'Marketing', section: 'monthlyReport' },
        { key: '/monthly-reports/jobs', label: 'Jobs', section: 'reportJobs' },
        { key: '/monthly-reports/hashtags', label: 'Trending Hashtags', section: 'reportHashtags' },
        { key: '/monthly-reports/wallet', label: 'Wallet', section: 'reportWallet' },
        { key: '/monthly-reports/creatives', label: 'Creatives', section: 'reportCreatives' },
        { key: '/monthly-reports/suppliers', label: 'Suppliers', section: 'reportSuppliers' },
        { key: '/monthly-reports/onemall', label: 'OneMall', section: 'reportOneMall' },
      ],
    },
    {
      // Voice/video calls and Go Live (docs/calls-and-live-plan.md §8).
      key: 'calls-live-group',
      icon: <VideoCameraOutlined />,
      label: 'Calls & Live',
      children: [
        { key: '/live', label: 'Live', section: 'live' },
        { key: '/calls', label: 'Calls', section: 'calls' },
      ],
    },
    {
      key: 'marketing-group',
      icon: <RiseOutlined />,
      label: 'Marketing',
      children: [
        { key: '/notification-campaigns', label: 'Notification Campaigns', section: 'notificationCampaigns' },
        { key: '/hashtags', label: 'Trending Hashtags', section: 'hashtags' },
        { key: '/synthetic-bots', label: 'Synthetic Bots', section: 'syntheticBots' },
        { key: '/growth-campaigns', label: 'Growth Campaigns', section: 'growthCampaigns' },
        { key: '/affiliate-campaigns', label: 'Affiliate Referral Campaigns', section: 'affiliateCodes' },
      ],
    },
    {
      // Wallet, Creatives and Suppliers moved in here from the top level (Todd,
      // 2026-10-27), same URLs and sections. The rest are placeholders
      // (config/upcomingModules.ts), Super Admin and Admin only.
      key: 'mini-apps-group',
      icon: <AppstoreOutlined />,
      label: 'Mini-Apps',
      // Carries the Suppliers badge up, so the icons-only rail still shows it.
      badge: pendingSuppliers,
      children: [
        { key: '/wallets', label: 'Wallet', section: 'wallets' },
        {
          key: 'creatives-group',
          label: 'Creatives',
          children: [
            { key: '/creative/analytics', label: 'Overview', section: 'creativeAnalytics' },
            { key: '/creative/upload', label: 'Creator Upload', section: 'creativeCatalogue' },
            { key: '/creative/manage', label: 'Catalogue', section: 'creativeCatalogue' },
            { key: '/creative/videos', label: 'Video Moderation', section: 'creativeModeration' },
            { key: '/creative/creators', label: 'Creator Applications', section: 'creativeCreators' },
            { key: '/creative/pipeline', label: 'Pipeline Health', section: 'creativePipeline' },
          ],
        },
        {
          key: 'suppliers-group',
          label: 'Suppliers',
          badge: pendingSuppliers,
          children: [
            { key: '/suppliers', label: 'All Suppliers', section: 'suppliers' },
            { key: '/supplier-categories', label: 'Categories', section: 'supplierCategories' },
            { key: '/supplier-plans', label: 'Plans', section: 'supplierPlans' },
            { key: '/enquiries', label: 'Enquiries', section: 'supplierEnquiries' },
          ],
        },
        {
          // Shop products owned by ordinary users (not Suppliers).
          key: 'onemall-group',
          label: 'OneMall',
          children: [
            { key: '/onemall/overview', label: 'Overview', section: 'oneMall' },
            { key: '/onemall/products', label: 'Products', section: 'oneMall', supplyOnly: true },
            { key: '/onemall/sellers', label: 'Sellers', section: 'oneMall', supplyOnly: true },
            { key: '/onemall/enquiries', label: 'Enquiries', section: 'oneMall', supplyOnly: true },
          ],
        },
        ...toLeaves(UPCOMING_MINI_APPS),
      ],
    },
    {
      // Jobs moved in here from the top level (same URL and section); the rest
      // are placeholders, Super Admin and Admin only.
      key: 'business-group',
      icon: <ProjectOutlined />,
      label: 'Business Modules',
      children: [{ key: '/jobs', label: 'Jobs', section: 'jobs' }, ...toLeaves(UPCOMING_BUSINESS_MODULES)],
    },
    {
      // High Ticket Ad Space (docs/high-ticket-ad-space.md §6).
      key: 'advertising-group',
      icon: <NotificationOutlined />,
      label: 'Advertising',
      children: [
        { key: '/advertising/bookings', label: 'Bookings', section: 'advertising' },
        { key: '/advertising/calendar', label: 'Slot Calendar', section: 'advertising' },
        { key: '/advertising/creatives', label: 'Creatives', section: 'advertising' },
        { key: '/advertising/metrics', label: 'Campaign Metrics', section: 'advertising' },
        { key: '/advertising/advertisers', label: 'Advertisers', section: 'advertising' },
        { key: '/advertising/reports', label: 'Ad Reports', section: 'advertising' },
      ],
    },
    { key: '/reports', icon: <WarningOutlined />, label: 'Reports', section: 'reports' },
    { key: '/vendors', icon: <ShopOutlined />, label: 'Vendors', section: 'vendors' },
    { key: '/admins', icon: <SafetyCertificateOutlined />, label: 'Admins', section: 'admins' },
    {
      key: 'settings-group',
      icon: <SettingOutlined />,
      label: 'Settings',
      children: [
        { key: '/settings', label: 'App Settings', section: 'appSettings' },
        { key: '/onboarding', label: 'Onboarding', section: 'onboarding' },
      ],
    },
  ]

  const sectionedItems = allItems.flatMap((item): Item[] => {
    if ('children' in item) {
      // A sub-group vanishes when none of its pages are visible; the group
      // vanishes when nothing at all is left in it.
      const children = item.children.flatMap((c): GroupChild[] => {
        if (!isSubGroup(c)) return visibleLeaf(c) ? [c] : []
        const leaves = c.children.filter(visibleLeaf)
        return leaves.length > 0 ? [{ ...c, children: leaves }] : []
      })
      return children.length > 0 ? [{ ...item, children }] : []
    }
    return visible(item.section) ? [item] : []
  })

  // Message Center doesn't belong to the role-permission matrix every other item
  // above is gated by (`visible(section)`) — it's a separate, narrower two-person
  // email allowlist (MESSAGE_CENTER_ALLOWED_EMAILS), checked server-side on every
  // request regardless of what this renders. This query is purely cosmetic: it
  // decides whether the link appears, not whether the page works.
  const { data: messageCenterAccess } = useQuery({
    queryKey: ['message-center', 'access'],
    queryFn: () => messageCenterApi.checkAccess(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  // Algorithm page: same arrangement as the Message Center — its own allowlist
  // (ALGORITHM_ALLOWED_EMAILS), cosmetic here, enforced server-side.
  const { data: algorithmAccess } = useQuery({
    queryKey: ['algorithm', 'access'],
    queryFn: () => algorithmApi.checkAccess(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  // Platform snapshot report: only admins on all three allowlists (Admin Control,
  // Message Center, Algorithm) — cosmetic here, enforced server-side.
  const { data: platformSnapshotAccess } = useQuery({
    queryKey: ['platform-snapshot', 'access'],
    queryFn: () => platformSnapshotApi.checkAccess(),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const items: Item[] = (() => {
    let list = sectionedItems
    // Spliced in at a set position, not appended at the end — position, not
    // just presence, since neither is in `allItems` above and so isn't placed
    // by that array's own ordering.
    const insertAfter = (key: string, item: Item) => {
      const index = list.findIndex((i) => i.key === key)
      const at = index === -1 ? list.length : index + 1
      list = [...list.slice(0, at), item, ...list.slice(at)]
    }
    if (messageCenterAccess?.data.data.allowed) {
      // Right after Users (above Marketing).
      insertAfter('/users', {
        key: '/message-center',
        icon: <MessageOutlined />,
        label: 'Message Center',
        // Filler to satisfy the Leaf type only — this item already bypassed
        // `visible()` above and is never re-filtered by it.
        section: 'users' as const,
      })
    }
    if (platformSnapshotAccess?.data.data.allowed) {
      // Under Dashboard; Algorithm (inserted next, also after Dashboard) lands above it.
      insertAfter('/dashboard', {
        key: '/platform-snapshot',
        icon: <FileTextOutlined />,
        label: 'Platform Snapshot',
        section: 'dashboard' as const,
      })
    }
    if (algorithmAccess?.data.data.allowed) {
      // Directly under Dashboard (Todd, 2026-10-26).
      insertAfter('/dashboard', {
        key: '/algorithm',
        icon: <ApartmentOutlined />,
        label: 'Algorithm',
        section: 'dashboard' as const,
      })
    }
    return list
  })()

  // The group holding the current page starts open, and so does its sub-group
  // if the page sits in one; the others start closed. Sub-groups share the one
  // set of open keys with groups (their keys don't collide).
  const groupOfPage = items.find((i) => 'children' in i && holdsPage(pathname, i.children))
  const subGroupOfPage =
    groupOfPage && 'children' in groupOfPage
      ? groupOfPage.children.find((c) => isSubGroup(c) && holdsPage(pathname, c.children))?.key
      : undefined
  const pageGroupKeys = [groupOfPage?.key, subGroupOfPage].filter((k): k is string => !!k)
  const pageGroupKeysId = pageGroupKeys.join('|')
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(pageGroupKeys))
  useEffect(() => {
    const keys = pageGroupKeysId ? pageGroupKeysId.split('|') : []
    setOpenGroups((s) => (keys.every((k) => s.has(k)) ? s : new Set([...s, ...keys])))
  }, [pageGroupKeysId])
  const toggleGroup = (key: string) =>
    setOpenGroups((s) => {
      const next = new Set(s)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // Touch-only tablets can't hover, so the rail gets an expand button there
  // (CSS shows it only for `any-hover: none`). It closes on a tap outside,
  // Escape, or following a link — as on the website.
  const [touchOpen, setTouchOpen] = useState(false)
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => setTouchOpen(false), [pathname])
  useEffect(() => {
    if (!touchOpen) return
    const onDown = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setTouchOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setTouchOpen(false)
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [touchOpen])

  const navLink = (key: string, icon: ReactNode, label: string, badge?: number) => {
    const active = isActive(pathname, key)
    const count = formatBadge(badge)
    return (
      <Link to={key} className="fh-nav-link" aria-current={active ? 'page' : undefined}>
        <span className="fh-nav-disc">
          {icon}
          {count && <span className="fh-nav-badge">{count}</span>}
        </span>
        <span className="fh-nav-label">{label}</span>
      </Link>
    )
  }

  const subLink = (leaf: Leaf) => (
    <Link to={leaf.key} className="fh-nav-sublink" aria-current={isActive(pathname, leaf.key) ? 'page' : undefined}>
      {leaf.label}
    </Link>
  )

  // A group inside a group: a text row like the pages around it, with a chevron,
  // folding its own pages in and out — same button + aria-expanded pattern as a
  // top-level group, so keyboard and screen readers treat it the same way.
  const subGroup = (group: SubGroup) => {
    const open = openGroups.has(group.key)
    const count = formatBadge(group.badge)
    return (
      <li key={group.key}>
        <button
          type="button"
          className="fh-nav-sublink fh-nav-subgroup"
          aria-expanded={open}
          data-holds-page={holdsPage(pathname, group.children) || undefined}
          onClick={() => toggleGroup(group.key)}
        >
          <span className="fh-nav-subgroup-label">{group.label}</span>
          {count && <span className="fh-nav-subbadge">{count}</span>}
          <DownOutlined className="fh-nav-chevron" />
        </button>
        {open && (
          <ul className="fh-nav-sub fh-nav-sub--nested">
            {group.children.map((c) => (
              <li key={c.key}>{subLink(c)}</li>
            ))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <nav
      ref={navRef}
      className={`fh-nav fh-nav--${variant}${variant === 'rail' ? ' fh-glass' : ''}`}
      data-rail-open={touchOpen || undefined}
      aria-label="Admin navigation"
    >
      <Link to={homePath} className="fh-nav-logo" aria-label="FursaHub Admin home">
        <img src={iconUrl} alt="" className="fh-nav-logo-mark" />
        <img src={logoUrl} alt="" className="fh-nav-logo-full" />
      </Link>

      <ul className="fh-nav-list">
        {items.map((item) => {
          if (!('children' in item)) return <li key={item.key}>{navLink(item.key, item.icon, item.label, item.badge)}</li>

          const open = openGroups.has(item.key)
          const count = formatBadge(item.badge)
          return (
            <li key={item.key}>
              <button
                type="button"
                className="fh-nav-link"
                aria-expanded={open}
                data-holds-page={holdsPage(pathname, item.children) || undefined}
                onClick={() => toggleGroup(item.key)}
              >
                <span className="fh-nav-disc">
                  {item.icon}
                  {count && <span className="fh-nav-badge">{count}</span>}
                </span>
                <span className="fh-nav-label">{item.label}</span>
                <DownOutlined className="fh-nav-chevron" />
              </button>
              {open && (
                <ul className="fh-nav-sub">
                  {item.children.map((c) => (isSubGroup(c) ? subGroup(c) : <li key={c.key}>{subLink(c)}</li>))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

      <div className="fh-nav-footer">
        {/* Every admin manages their own 2FA, whatever their role. */}
        {navLink('/security', <LockOutlined />, 'Security')}
        {variant === 'rail' && (
          <button
            type="button"
            className="fh-nav-link fh-rail-toggle"
            aria-expanded={touchOpen}
            onClick={() => setTouchOpen((o) => !o)}
          >
            <span className="fh-nav-disc">
              <RightOutlined className="fh-rail-toggle-icon" />
            </span>
            <span className="fh-nav-label">{touchOpen ? 'Collapse menu' : 'Expand menu'}</span>
          </button>
        )}
        <MoreMenu />
      </div>
    </nav>
  )
}
